// TUNE's bulletin board on the home page: news, releases and what TUNE is working on, the newest first. Three
// columns on a desktop; on a phone, one at a time, chosen with a segmented control.
import type { components } from '@tune/shared';
import { useState } from 'react';
import { Badge, Button, SegmentedControl } from '../../ui';
import s from './Board.module.css';

type Bulletin = components['schemas']['Bulletin'];
type Post = components['schemas']['BulletinPost'];
export type ColumnId = 'news' | 'releases' | 'development';

/** The board's columns: their names, short names for a phone's segments, and what an empty one says. */
export const BULLETIN_COLUMNS: ReadonlyArray<{ id: ColumnId; label: string; short: string; empty: string }> = [
  { id: 'news', label: 'News', short: 'News', empty: 'No news yet.' },
  { id: 'releases', label: 'Latest releases', short: 'Releases', empty: 'No releases announced yet.' },
  { id: 'development', label: 'In the works', short: 'In the works', empty: 'Nothing in the works to share yet.' },
];

/** How many posts a column shows before "Show more". */
const SHOWN = 3;

/** A post's day: "9 October 2026". */
export function postDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Dated in the last fortnight. */
export function isRecent(date: string, now = Date.now()): boolean {
  const age = (now - new Date(`${date}T00:00:00`).getTime()) / 86_400_000;
  return age >= 0 && age < 14;
}

/** A post as owners see it, its rest behind Read more. */
export function PostView({ post }: { post: Post }) {
  const [open, setOpen] = useState(false);
  return (
    <article className={s.post}>
      <div className={s.meta}>
        <time dateTime={post.date}>{postDay(post.date)}</time>
        {isRecent(post.date) && <Badge tone="accent">New</Badge>}
      </div>
      <h4 className={s.postTitle}>{post.title}</h4>
      <p className={s.body}>{post.body}</p>
      {open && <p className={s.more}>{post.more}</p>}
      {post.more && (
        <Button
          variant="plain"
          size="small"
          className={s.toggle}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Show less' : 'Read more'}
        </Button>
      )}
    </article>
  );
}

function Column({ id, posts }: { id: ColumnId; posts: Post[] }) {
  const [all, setAll] = useState(false);
  const column = BULLETIN_COLUMNS.find((c) => c.id === id);
  const shown = all ? posts : posts.slice(0, SHOWN);
  if (posts.length === 0) {
    return <p className={s.empty}>{column?.empty}</p>;
  }
  return (
    <>
      <ul className={s.posts} role="list">
        {shown.map((post) => (
          <li key={post.id}>
            <PostView post={post} />
          </li>
        ))}
      </ul>
      {posts.length > SHOWN && (
        <Button variant="plain" size="small" className={s.toggle} onClick={() => setAll((a) => !a)}>
          {all ? 'Show fewer' : `Show ${posts.length - SHOWN} more`}
        </Button>
      )}
    </>
  );
}

export function BulletinBoard({ board }: { board: Bulletin }) {
  const [column, setColumn] = useState<ColumnId>('news');
  return (
    <section className={s.board} aria-labelledby="bulletin-board">
      <div className={s.head}>
        <h2 id="bulletin-board" className={s.title}>
          Bulletin board
        </h2>
        <p className={s.lead}>From TUNE: news, releases and what we're working on.</p>
      </div>
      <div className={s.columns}>
        {BULLETIN_COLUMNS.map((c) => (
          <section key={c.id} className={s.column} aria-labelledby={`bulletin-${c.id}`}>
            <h3 id={`bulletin-${c.id}`} className={s.columnTitle}>
              {c.label}
            </h3>
            <Column id={c.id} posts={board[c.id]} />
          </section>
        ))}
      </div>
      <div className={s.phone}>
        <SegmentedControl
          label="Bulletin board"
          wide
          value={column}
          onChange={setColumn}
          segments={BULLETIN_COLUMNS.map((c) => ({ value: c.id, label: c.short }))}
        />
        <div className={s.column}>
          <h3 className="visually-hidden">{BULLETIN_COLUMNS.find((c) => c.id === column)?.label}</h3>
          <Column id={column} posts={board[column]} />
        </div>
      </div>
    </section>
  );
}
